import type {
  Contact,
  DocumentFields,
  DocumentKind,
  LineAmountType,
  LineItemInput,
  XeroContactPayload,
  XeroInvoicePayload,
  XeroItemPayload,
  XeroLineItem,
  XeroPayload,
  XeroPurchaseOrderPayload,
  XeroQuotePayload,
} from './types.js';
import { normaliseDate, parseAmount } from './normalize.js';

/** Forms that carry a LineItems[] array. */
export const FORMS_WITH_LINES: ReadonlySet<DocumentKind> = new Set<DocumentKind>([
  'invoice',
  'bill',
  'quote',
  'po',
]);

/** Discounts are accepted on sales documents only. */
export const FORMS_WITH_DISCOUNT: ReadonlySet<DocumentKind> = new Set<DocumentKind>([
  'invoice',
  'quote',
]);

export const ENDPOINTS: Record<DocumentKind, string> = {
  invoice: '/Invoices',
  bill: '/Invoices',
  quote: '/Quotes',
  po: '/PurchaseOrders',
  contact: '/Contacts',
  item: '/Items',
};

function str(fields: DocumentFields, key: string): string {
  const v = fields[key];
  return typeof v === 'string' ? v.trim() : '';
}

function optional(value: string): string | undefined {
  return value === '' ? undefined : value;
}

function bool(fields: DocumentFields, key: string): boolean {
  return fields[key] === true;
}

/**
 * The shared line model. Invoices, bills, quotes and purchase orders all map
 * through here, so a change to line handling lands on all four at once.
 */
export function mapLines(
  kind: DocumentKind,
  lines: LineItemInput[],
  /**
   * How the document's amounts are stated.
   *
   * NoTax means exactly that, and Xero enforces it on the lines as well as
   * the header: "The TaxType code INPUT cannot be used when Line Amount
   * Types is set to NoTax." It refuses the whole document for it.
   *
   * So the tax type is dropped here rather than being left for Xero to
   * complain about. The form still remembers what was chosen, because the
   * header can be switched back and the rate should still be there when
   * it is.
   */
  amountsAre: LineAmountType = 'Exclusive',
): XeroLineItem[] {
  const allowDiscount = FORMS_WITH_DISCOUNT.has(kind);
  const taxed = amountsAre !== 'NoTax';

  return lines.map((line) => {
    const mapped: XeroLineItem = {
      Description: line.description,
      Quantity: parseAmount(line.qty),
      UnitAmount: parseAmount(line.price),
      ...(taxed ? { TaxType: line.taxType } : {}),
    };
    // Omitted rather than sent empty. Xero accepts an empty account code and
    // files the line against nothing, which is how a bill arrived in a client's
    // books with a blank Account column — accepted, wrong, and silent.
    if (line.accountCode) mapped.AccountCode = line.accountCode;
    if (line.itemCode) mapped.ItemCode = line.itemCode;

    const discount = parseAmount(line.disc);
    if (allowDiscount && discount) mapped.DiscountRate = discount;

    return mapped;
  });
}

function contactRef(fields: DocumentFields, contacts: Contact[]) {
  const id = str(fields, 'contactId');
  return { ContactID: id, Name: contacts.find((c) => c.id === id)?.name ?? '' };
}

/**
 * How finished a document is when it reaches Xero.
 *
 * DRAFT sits in Xero waiting for someone to approve it there. AUTHORISED is a
 * real accounting document the moment it lands: it appears in the ledger, it
 * counts towards what a customer owes, and it can be paid.
 *
 * Which one is allowed is a question about the person, not the document, and
 * is decided before this is called — see documents.ts. This only builds what
 * it is told to.
 */
export type FilingStatus = 'DRAFT' | 'AUTHORISED';

/**
 * Build the Xero payload for one entry form.
 *
 * Empty optional fields are omitted rather than sent as "" so Xero does not
 * overwrite an existing value with a blank on a PUT.
 */
export function buildPayload(
  kind: DocumentKind,
  fields: DocumentFields,
  lines: LineItemInput[],
  amountsAre: LineAmountType,
  contacts: Contact[],
  /*
    Approved unless told otherwise.

    It used to default to DRAFT, on the reasoning that the cautious default
    is the one that waits for a human. There is no human waiting any more:
    the practice removed the draft choice, because a document sitting
    unapproved in Xero is work done once here and then again there, and the
    second half is the half that gets forgotten.

    Quotes and purchase orders below are still built DRAFT, and that is not
    this decision — a quote's own lifecycle begins as a draft and becomes
    sent, then accepted. Nobody is reviewing it on CFOAI's behalf.
  */
  status: FilingStatus = 'AUTHORISED',
): XeroPayload {
  switch (kind) {
    case 'invoice':
    case 'bill': {
      const payload: XeroInvoicePayload = {
        Type: kind === 'invoice' ? 'ACCREC' : 'ACCPAY',
        Contact: contactRef(fields, contacts),
        Date: normaliseDate(str(fields, 'date')),
        DueDate: optional(normaliseDate(str(fields, 'dueDate'))),
        Reference: optional(str(fields, 'reference')),
        CurrencyCode: str(fields, 'currencyCode'),
        LineAmountTypes: amountsAre,
        Status: status,
        LineItems: mapLines(kind, lines, amountsAre),
      };
      if (kind === 'invoice') {
        const number = optional(str(fields, 'invoiceNumber'));
        if (number) payload.InvoiceNumber = number;
      }
      return payload;
    }

    case 'quote': {
      const payload: XeroQuotePayload = {
        Contact: contactRef(fields, contacts),
        Date: normaliseDate(str(fields, 'date')),
        ExpiryDate: optional(normaliseDate(str(fields, 'expiryDate'))),
        QuoteNumber: optional(str(fields, 'quoteNumber')),
        Title: optional(str(fields, 'titleText')),
        Summary: optional(str(fields, 'summary')),
        Reference: optional(str(fields, 'reference')),
        CurrencyCode: str(fields, 'currencyCode'),
        LineAmountTypes: amountsAre,
        Terms: optional(str(fields, 'terms')),
        Status: 'DRAFT',
        LineItems: mapLines(kind, lines, amountsAre),
      };
      return payload;
    }

    case 'po': {
      const payload: XeroPurchaseOrderPayload = {
        Contact: contactRef(fields, contacts),
        Date: normaliseDate(str(fields, 'date')),
        DeliveryDate: optional(normaliseDate(str(fields, 'deliveryDate'))),
        PurchaseOrderNumber: optional(str(fields, 'orderNumber')),
        Reference: optional(str(fields, 'reference')),
        DeliveryAddress: optional(str(fields, 'deliveryAddress')),
        AttentionTo: optional(str(fields, 'attentionTo')),
        Telephone: optional(str(fields, 'telephone')),
        DeliveryInstructions: optional(str(fields, 'deliveryInstructions')),
        CurrencyCode: str(fields, 'currencyCode'),
        LineAmountTypes: amountsAre,
        Status: 'DRAFT',
        LineItems: mapLines(kind, lines, amountsAre),
      };
      return payload;
    }

    case 'item': {
      const tracked = bool(fields, 'tracked');
      const payload: XeroItemPayload = {
        Code: str(fields, 'code'),
        Name: str(fields, 'name'),
        IsTrackedAsInventory: tracked,
        PurchaseDetails: {
          UnitPrice: parseAmount(str(fields, 'buyPrice')),
          AccountCode: str(fields, 'buyAccount'),
          TaxType: str(fields, 'buyTax'),
        },
        PurchaseDescription: optional(str(fields, 'buyDesc')),
        SalesDetails: {
          UnitPrice: parseAmount(str(fields, 'sellPrice')),
          AccountCode: str(fields, 'sellAccount'),
          TaxType: str(fields, 'sellTax'),
        },
        Description: optional(str(fields, 'sellDesc')),
      };
      /*
        Both of these, or neither.

        Xero rejects an inventory asset account on an untracked item, and
        refuses to create a tracked one without a cost of goods sold
        account — "The COGSAccountCode in PurchaseDetails is also required
        to create a tracked item", in its own words. Only the first half
        of that was here, so every tracked item this form tried to create
        was refused.
      */
      if (tracked) {
        payload.InventoryAssetAccountCode = str(fields, 'inventoryAccount');
        payload.PurchaseDetails.COGSAccountCode = str(fields, 'cogsAccount');
      }
      return payload;
    }

    case 'contact': {
      const phone = str(fields, 'phone');
      const payload: XeroContactPayload = {
        Name: str(fields, 'name'),
        AccountNumber: optional(str(fields, 'accountNumber')),
        FirstName: optional(str(fields, 'firstName')),
        LastName: optional(str(fields, 'lastName')),
        EmailAddress: optional(str(fields, 'email')),
        Addresses: [
          {
            AddressType: 'STREET',
            AddressLine1: str(fields, 'street'),
            City: str(fields, 'city'),
            Region: str(fields, 'region'),
            PostalCode: str(fields, 'postcode'),
            Country: str(fields, 'country'),
          },
        ],
        BankAccountDetails: optional(str(fields, 'bankAccount')),
        TaxNumber: optional(str(fields, 'taxNumber')),
        DefaultCurrency: str(fields, 'defaultCurrency'),
      };
      if (phone) payload.Phones = [{ PhoneType: 'DEFAULT', PhoneNumber: phone }];
      return payload;
    }
  }
}

/** The array wrapper Xero expects on the accounting endpoints. */
export function wrapForXero(kind: DocumentKind, payload: XeroPayload): Record<string, unknown> {
  const key: Record<DocumentKind, string> = {
    invoice: 'Invoices',
    bill: 'Invoices',
    quote: 'Quotes',
    po: 'PurchaseOrders',
    contact: 'Contacts',
    item: 'Items',
  };
  return { [key[kind]]: [payload] };
}
